// Service hook (TS-2.5.01, Resource Accessor layer) - the only place this feature calls
// dataQualityApi. Mirrors useRoadmap/useProjects' shape: fetch on mount and whenever the caller's
// memoized query changes, refetch on any push-channel invalidation (a write anywhere can change
// what is wrong with the portfolio), surface a typed ApiError rather than throwing.
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../api/ApiError";
import type { DataQualityReport } from "../../../api/generated/contracts";
import type { DataQualityParams } from "../../../api/dataQuality";
import { dataQualityApi } from "../../../api/dataQuality";
import { useInvalidation } from "../../../realtime/useInvalidation";

export interface UseDataQualityResult {
  readonly report: DataQualityReport | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
}

/** `params` must be caller-memoized, the same contract `useRoadmap`/`useProjects` place on their own filter argument. */
export function useDataQuality(params: DataQualityParams): UseDataQualityResult {
  const [report, setReport] = useState<DataQualityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    void dataQualityApi
      .get(params)
      .then((next) => {
        setReport(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The data-quality report could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(refresh, [refresh]);
  useInvalidation(refresh);

  return { report, loading, error };
}
